<script setup>
import moment from 'moment'
import { mediaUrl } from '@core/utils/mediaUrl'
import { sanitizeHtml } from '@core/utils/sanitizeHtml'
import {useUserGroupsStore, useUsersStore, useReservationsStore} from '@/stores'
import AdminEditUserSubscriptionDialog from "@core/components/AdminEditUserSubscriptionDialog.vue"
import { simulation, buildProfileUrl, getFullUrl, displayPhone } from '@/plugins/misc'
import axios from "@axios"
import { useDebounce } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

const usersStore = useUsersStore()
let myUser = null
let editingUser = null
let fetching = null;
({ myUser, editingUser, fetching  } = storeToRefs(usersStore))

const userGroupsStore = useUserGroupsStore()
let userGroups = null;
({  userGroups  } = storeToRefs(userGroupsStore))

const reservationsStore = useReservationsStore()

const usersPerPage = 40

const searchValue = ref("")
const debouncedSearchText = useDebounce(searchValue, 600)

const currentPage = ref(1)
const orderBy = ref("bookingsTotal")
const orderDirection = ref("-") // this should be "" or "-" for desc
const primarySecondaryFiltering = ref("")
const hasSecondaryAccountFiltering = ref("")
const subscriptionStatusFiltering = ref("")
const groupFiltering = ref("")
const minFavoritesFilter = ref("")
const friendsBucketFilter = ref("")
const activeTab = ref("Platinum")

// Server-paginated state
const displayedUsers = ref([])
const totalCount = ref(0)
const cohortStats = ref(null)
const hasMore = ref(true)
const loadingMore = ref(false)
const scrollSentinel = ref(null)

// Request counter to prevent stale responses from overwriting newer ones
let fetchRequestId = 0

const isConfirmDialogOpen = ref(false)
const selectedUser = ref(null)

const isProfileSummaryDialogOpen = ref(false)
const profileSummaryUser = ref(null)
const profileSummaryRef = ref(null)
const copySuccess = ref(false)
const copiedReferralUserId = ref(null)

const openProfileSummary = function(user) {
  profileSummaryUser.value = user
  isProfileSummaryDialogOpen.value = true
}

const copyProfileSummary = async function() {
  const el = profileSummaryRef.value
  if (!el) return

  const html = el.innerHTML
  const text = el.innerText

  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' })
      })
    ])
    copySuccess.value = true
    setTimeout(() => { copySuccess.value = false }, 2000)
  } catch (err) {
    try {
      await navigator.clipboard.writeText(text)
      copySuccess.value = true
      setTimeout(() => { copySuccess.value = false }, 2000)
    } catch (e) {
      console.error('Copy failed:', e)
    }
  }
}

const { t } = useI18n()

const copyReferralMessage = function(user) {
  if (!user.profileId) return
  const message = `${t('menu.referral.referralMessage')} https://reserverm.com/${user.profileId}/friends`
  navigator.clipboard.writeText(message)
  copiedReferralUserId.value = user._id
  setTimeout(() => {
    copiedReferralUserId.value = null
  }, 2000)
}

const userTypeUiColors = {
  Draft: 'primary',
  Submitted: 'primary',
  Accepted: 'success',
  Processing: 'secondary',
  "Awaiting Response from Restaurant": 'secondary',
  "In Review": 'secondary',
  "Member Action": 'secondary',
  "Not Available": 'warning',
  "No Response": 'warning',
  "Declined": 'warning'
}


function escapeCsvField(value) {
  if (value == null) return ''
  const str = String(value)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }

  return str
}

const exporting = ref(false)

const exportFilteredUsers = async function() {
  if (exporting.value) return
  exporting.value = true

  try {
    // Fetch all filtered users (not just current page)
    const params = {
      tab: activeTab.value,
      sortBy: orderBy.value,
      sortDirection: orderDirection.value === '-' ? 'desc' : 'asc',
      export: 'true',
    }

    if (debouncedSearchText.value) params.searchTerm = debouncedSearchText.value
    if (subscriptionStatusFiltering.value) params.subscriptionStatus = subscriptionStatusFiltering.value
    if (primarySecondaryFiltering.value) params.primarySecondary = primarySecondaryFiltering.value
    if (hasSecondaryAccountFiltering.value) params.hasSecondaryAccount = hasSecondaryAccountFiltering.value
    if (groupFiltering.value) params.group = groupFiltering.value
    if (minFavoritesFilter.value) params.minFavorites = minFavoritesFilter.value
    if (friendsBucketFilter.value) params.friendsBucket = friendsBucketFilter.value

    const result = await usersStore.getAdminUsers(params)
    const items = result.users
    if (!items.length) return

    const columns = [
      { header: 'Name', value: u => u.fullName },
      { header: 'Email', value: u => u.email },
      { header: 'Company', value: u => u.company },
      { header: 'Position', value: u => u.title },
      { header: 'Phone', value: u => u.phoneNumber },
      { header: 'LinkedIn', value: u => u.profileLinkedInProfileId ? `linkedin.com/in/${u.profileLinkedInProfileId}` : '' },
      { header: 'Instagram', value: u => u.profileInstagramProfileId ? `instagram.com/${u.profileInstagramProfileId}` : '' },
      { header: 'Website', value: u => u.profileWebsiteLink },
      { header: 'Bio', value: u => u.bio },
      { header: 'Date Joined', value: u => (u.createdAt || u.subscriptionStart) ? moment(u.createdAt || u.subscriptionStart).format('YYYY-MM-DD') : '' },
      { header: 'Member Since', value: u => u.memberSince ? moment(u.memberSince).format('YYYY-MM-DD') : '' },
      { header: 'Last Active', value: u => u.lastActiveAt ? moment(u.lastActiveAt).format('YYYY-MM-DD') : '' },
      { header: 'Membership Type', value: u => u.membershipType },
      { header: 'Group', value: u => u.primaryGroupName || 'Individual' },
      { header: 'Primary/Secondary', value: u => u.isSecondaryAccount ? 'Secondary' : 'Primary' },
      { header: 'Platinum Renewal Date', value: u => u.platinumRenewalDate ? moment(u.platinumRenewalDate).format('YYYY-MM-DD') : '' },
      { header: 'Bookings Last 90d', value: u => u.bookingsLast90d || 0 },
      { header: 'Bookings Last 365d', value: u => u.bookingsLast365d || 0 },
      { header: 'Bookings Total', value: u => u.bookingsTotal || 0 },
      { header: 'Favorites', value: u => u.favoritesCount || 0 },
      { header: 'Connections', value: u => u.followingCount || 0 },
      { header: 'Avg Spend/Person', value: u => u.avgSpendPerPerson || 0 },
      { header: 'Secondary Account Member', value: u => u.secondaryAccountMemberName },
      { header: 'Verified Tags', value: u => u.verifiedTags?.map(t => t.tagName).join('; ') },
      { header: 'RM Profile', value: u => u.profileId ? `reserverm.com/m/${u.profileId}` : '' },
      { header: 'RM Referral Link', value: u => u.profileId ? `reserverm.com/${u.profileId}/friends` : '' },
    ]

    const headerRow = columns.map(c => escapeCsvField(c.header)).join(',')
    const dataRows = items.map(u =>
      columns.map(c => escapeCsvField(c.value(u))).join(','),
    )

    const csvContent = [headerRow, ...dataRows].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'members.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } finally {
    exporting.value = false
  }
}

const openProfile = function(user) {
  window.open( `/m/${user.profileId}`, '_blank')
}


const groupFilteringOptions = computed(() => {
  let options = [{value:"", text:"All"}, {value:"individuals", text:"Individuals"}, {value:"groups", text:"All Groups"}]
  for(let group of userGroups.value){
    options.push({
      value: group.name,
      text: group.name
    })
  }

  return options
})

// Reset secondary account filter when not on primary
watch(primarySecondaryFiltering, val => {
  if (val !== 'primary') hasSecondaryAccountFiltering.value = ""
})

// Fetch users from server with current filters
const fetchUsers = async function(append = false) {
  if (append && (!hasMore.value || loadingMore.value)) return
  if (append) loadingMore.value = true

  const thisRequestId = ++fetchRequestId

  const params = {
    page: currentPage.value,
    limit: usersPerPage,
    tab: activeTab.value,
    sortBy: orderBy.value,
    sortDirection: orderDirection.value === '-' ? 'desc' : 'asc',
  }

  if (debouncedSearchText.value) {
    params.searchTerm = debouncedSearchText.value
  }
  if (subscriptionStatusFiltering.value) params.subscriptionStatus = subscriptionStatusFiltering.value
  if (primarySecondaryFiltering.value) params.primarySecondary = primarySecondaryFiltering.value
  if (hasSecondaryAccountFiltering.value) params.hasSecondaryAccount = hasSecondaryAccountFiltering.value
  if (groupFiltering.value) params.group = groupFiltering.value
  if (minFavoritesFilter.value) params.minFavorites = minFavoritesFilter.value
  if (friendsBucketFilter.value) params.friendsBucket = friendsBucketFilter.value

  const result = await usersStore.getAdminUsers(params)

  // Ignore stale responses — only apply if this is still the latest request
  if (thisRequestId !== fetchRequestId) {
    loadingMore.value = false
    
    return
  }

  if (append) {
    displayedUsers.value = [...displayedUsers.value, ...result.users]
  } else {
    displayedUsers.value = result.users
  }

  totalCount.value = result.totalCount
  cohortStats.value = result.cohortStats || null
  hasMore.value = currentPage.value * usersPerPage < result.totalCount
  loadingMore.value = false
}

const loadMore = function() {
  if (!hasMore.value || loadingMore.value) return
  currentPage.value++
  fetchUsers(true)
}

const toggleUserApproval = async function(userId) {
  await usersStore.adminToggleApproval(userId)
  await fetchUsers()
}

// When filters/sort/search change, clear stale results immediately and reset to page 1
watch([activeTab, subscriptionStatusFiltering, primarySecondaryFiltering, hasSecondaryAccountFiltering, groupFiltering, minFavoritesFilter, friendsBucketFilter, orderBy, orderDirection, debouncedSearchText], () => {
  displayedUsers.value = []
  totalCount.value = 0
  cohortStats.value = null
  hasMore.value = true
  currentPage.value = 1
  fetchUsers()
})

// Intersection observer for infinite scroll
let observer = null

onMounted(() => {
  fetchUsers()

  observer = new IntersectionObserver(entries => {
    if (entries[0].isIntersecting) {
      loadMore()
    }
  }, { rootMargin: '200px' })

  watch(scrollSentinel, el => {
    if (el) observer.observe(el)
  }, { immediate: true })
})

onBeforeUnmount(() => {
  if (observer) observer.disconnect()
})
</script>

<template>
  <div>
    <AdminEditUserDialog />
    <AdminEditUserSubscriptionDialog />
    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmDialogOpen"
      confirmation-msg="Are you sure you want to delete this member? This is not reversible!"
      @confirm="usersStore.adminDeleteUser(selectedUser._id)"
    />

    <!-- Profile Summary Dialog -->
    <VDialog
      v-model="isProfileSummaryDialogOpen"
      max-width="520"
    >
      <VCard class="profile-summary-dialog">
        <div class="profile-summary-dialog-header">
          <div class="profile-summary-dialog-title">
            {{ profileSummaryUser?.firstName }} {{ profileSummaryUser?.lastName }} - RM Profile Details
          </div>
          <VBtn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="isProfileSummaryDialogOpen = false"
          />
        </div>
        <VDivider />
        <VCardText class="pa-6">
          <template v-if="profileSummaryUser">
            <!-- Avatar + Name (not included in copy) -->
            <div class="d-flex align-center mb-4">
              <div class="profile-summary-avatar rounded-circle mr-3">
                <VImg
                  v-if="profileSummaryUser.profileImageId"
                  class="profile-summary-avatar-img"
                  :src="mediaUrl(profileSummaryUser.profileImageId, 'small')"
                  cover
                />
                <div
                  v-else
                  class="profile-summary-avatar-fallback d-flex align-center justify-center"
                >
                  <span>{{ profileSummaryUser.firstName?.[0]?.toUpperCase() }}{{ profileSummaryUser.lastName?.[0]?.toUpperCase() }}</span>
                </div>
              </div>
              <div class="profile-summary-name">
                {{ profileSummaryUser.fullName }}
              </div>
            </div>
          </template>
          <div
            ref="profileSummaryRef"
            class="profile-summary-content"
          >
            <template v-if="profileSummaryUser">
              <!-- Title (included in copy) -->
              <div>
                <strong>{{ profileSummaryUser.firstName }} {{ profileSummaryUser.lastName }} - RM Profile Details</strong>
              </div>
              <!-- Verified + Membership -->
              <div
                v-if="profileSummaryUser.membershipType"
                class="profile-summary-line"
              >
                ({{ profileSummaryUser.verified ? 'Verified + ' : '' }}{{ profileSummaryUser.membershipType }} member)
              </div>
              <!-- Phone -->
              <div
                v-if="profileSummaryUser.phoneNumber"
                class="profile-summary-line"
              >
                M. {{ displayPhone(profileSummaryUser.phoneNumber) }}
              </div>

              <!-- Email -->
              <div class="profile-summary-line">
                E. {{ profileSummaryUser.email }}
              </div>


              <!-- Title, Company -->
              <div
                v-if="profileSummaryUser.title || profileSummaryUser.company"
                class="profile-summary-line"
              >
                <template v-if="profileSummaryUser.title && profileSummaryUser.company">
                  {{ profileSummaryUser.title }}, {{ profileSummaryUser.company }}
                </template>
                <template v-else>
                  {{ profileSummaryUser.title || profileSummaryUser.company }}
                </template>
              </div>

              <!-- Title2, Company2 -->
              <div
                v-if="profileSummaryUser.title2 || profileSummaryUser.company2"
                class="profile-summary-line"
              >
                <template v-if="profileSummaryUser.title2 && profileSummaryUser.company2">
                  {{ profileSummaryUser.title2 }}, {{ profileSummaryUser.company2 }}
                </template>
                <template v-else>
                  {{ profileSummaryUser.title2 || profileSummaryUser.company2 }}
                </template>
              </div>

              <!-- Bio -->
              <div
                v-if="profileSummaryUser.bio"
                class="profile-summary-line mt-3"
              >
                <strong>Bio:</strong> {{ profileSummaryUser.bio }}
              </div>

              <!-- Verified Tags -->
              <div
                v-if="profileSummaryUser.verifiedTags?.length > 0"
                class="profile-summary-tags mt-3"
              >
                <div
                  v-for="tag in profileSummaryUser.verifiedTags"
                  :key="tag._id || tag.tagName"
                  class="profile-summary-tag-line"
                >
                  <strong>{{ tag.tagName }}:</strong> <span v-html="sanitizeHtml(tag.tagShortDescription || tag.tagDescription || 'Verified by RM')" />
                </div>
              </div>
              <!-- Links -->
              <div
                v-if="profileSummaryUser.profileId || profileSummaryUser.profileLinkedInProfileId || profileSummaryUser.profileInstagramProfileId || profileSummaryUser.profileXProfileId || profileSummaryUser.profileTikTokProfileId || profileSummaryUser.profileWebsiteLink"
                class="profile-summary-links mt-3"
              >
                <div
                  v-if="profileSummaryUser.profileLinkedInProfileId"
                  class="profile-summary-link-line"
                >
                  <strong>LinkedIn: </strong>
                  <span><a
                    :href="buildProfileUrl('linkedin', profileSummaryUser.profileLinkedInProfileId)"
                    target="_blank"
                    @click.stop=""
                  >linkedin.com/in/{{ profileSummaryUser.profileLinkedInProfileId }}</a></span>
                </div>
                <div
                  v-if="profileSummaryUser.profileInstagramProfileId"
                  class="profile-summary-link-line"
                >
                  <strong>Instagram:</strong> <a
                    :href="buildProfileUrl('instagram', profileSummaryUser.profileInstagramProfileId)"
                    target="_blank"
                    @click.stop=""
                  >instagram.com/{{ profileSummaryUser.profileInstagramProfileId }}</a>
                </div>
                <div
                  v-if="profileSummaryUser.profileXProfileId"
                  class="profile-summary-link-line"
                >
                  <strong>X:</strong> <a
                    :href="buildProfileUrl('twitter', profileSummaryUser.profileXProfileId)"
                    target="_blank"
                    @click.stop=""
                  >x.com/{{ profileSummaryUser.profileXProfileId }}</a>
                </div>
                <div
                  v-if="profileSummaryUser.profileTikTokProfileId"
                  class="profile-summary-link-line"
                >
                  <strong>TikTok:</strong> <a
                    :href="buildProfileUrl('tiktok', profileSummaryUser.profileTikTokProfileId)"
                    target="_blank"
                    @click.stop=""
                  >tiktok.com/@{{ profileSummaryUser.profileTikTokProfileId }}</a>
                </div>
                <div
                  v-if="profileSummaryUser.profileWebsiteLink"
                  class="profile-summary-link-line"
                >
                  <strong>Bio Page: </strong> <a
                    :href="getFullUrl(profileSummaryUser.profileWebsiteLink)"
                    target="_blank"
                    @click.stop=""
                  >{{ profileSummaryUser.profileWebsiteLink }}</a>
                </div>
                <div
                  v-if="profileSummaryUser.profileId"
                  class="profile-summary-link-line"
                >
                  <strong>RM: </strong> <a
                    :href="`/m/${profileSummaryUser.profileId}`"
                    target="_blank"
                    @click.stop=""
                  >reserverm.com/m/{{ profileSummaryUser.profileId }}</a>
                </div>
              </div>
            </template>
          </div>
        </VCardText>
        <VDivider />
        <VCardActions class="pa-4">
          <VSpacer />
          <VBtn
            variant="outlined"
            size="small"
            @click="isProfileSummaryDialogOpen = false"
          >
            Close
          </VBtn>
          <VBtn
            :color="copySuccess ? 'success' : 'primary'"
            :prepend-icon="copySuccess ? 'mdi-check' : 'mdi-content-copy'"
            size="small"
            @click="copyProfileSummary"
          >
            {{ copySuccess ? 'Copied!' : 'Copy Profile' }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Back to Restaurant Admin Button -->
    <div
      v-if="myUser?.profileId"
      class="content-wrapper mt-6 mb-n2"
    >
      <div class="back-button-container">
        <RouterLink
          to="/admin/profile"
          class="back-button d-flex align-center cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          <span class="ml-1">Back to Restaurant Admin</span>
        </RouterLink>
      </div>
    </div>

    <div class="grid-sidebar-left content-wrapper mt-6">
      <div>
        <VCard class="mb-4 pa-4 pt-8 pb-7">
          <div class="account-card">
            <div
              v-if="myUser"
              class="account-card-content"
            >
              <!--                <h2 class="account-card-name">{{ myUser.fullName ? myUser.fullName : myUser.email }}</h2> -->
              <!--                <span class="account-card-detail" v-if="myUser.userLevel == 'admin'">Admin since {{moment().format("MMM YYYY") }}</span> -->
              <!--                <span class="account-card-detail" v-else>Member since {{moment().format("MMM YYYY") }}</span> -->
              <!--                <span class="account-card-meta">{{ myUser.giftMessage }}</span> -->
              <!-- &lt;!&ndash;                <span class="account-card-meta">{{ myUser.giftMessage }}</span>&ndash;&gt; -->

              <div class="mb-2">
                Filters
              </div>
              <VSelect
                v-model="subscriptionStatusFiltering"
                class="mb-4"
                label="Subscription Status"
                item-title="text"
                item-value="value"
                :items="[{text:'Show all', value: ''},{text:'Current', value: 'current'},{text:'Expires Soon', value: 'expiresSoon'}]"
                required
              />
              <VSelect
                v-model="primarySecondaryFiltering"
                class="mb-4"
                label="Secondary/Primary"
                item-title="text"
                item-value="value"
                :items="[{text:'Show all', value: ''},{text:'Primary', value: 'primary'},{text:'Secondary', value: 'secondary'}]"
                required
              />
              <VSelect
                v-if="primarySecondaryFiltering == 'primary'"
                v-model="hasSecondaryAccountFiltering"
                class="mb-4"
                label="Secondary Account"
                item-title="text"
                item-value="value"
                :items="[{text:'Show all', value: ''},{text:'Set', value: 'set'},{text:'Unset', value: 'unset'}]"
                required
              />
              <VSelect
                v-model="groupFiltering"
                class="mb-4"
                label="Individual/Group"
                item-title="text"
                item-value="value"
                :items="groupFilteringOptions"
                required
              />
              <VSelect
                v-model="minFavoritesFilter"
                class="mb-4"
                label="Min Favorites"
                item-title="text"
                item-value="value"
                :items="[{text:'All', value: ''},{text:'1+', value: '1'},{text:'5+', value: '5'},{text:'10+', value: '10'},{text:'25+', value: '25'}]"
                required
              />
              <VSelect
                v-model="friendsBucketFilter"
                class="mb-4"
                label="Connections"
                item-title="text"
                item-value="value"
                :items="[{text:'All', value: ''},{text:'0', value: '0'},{text:'1–5', value: '1-5'},{text:'6–20', value: '6-20'},{text:'20+', value: '20+'}]"
                required
              />
              <VDivider class="mb-4" />
              <div class="mb-2">
                Sorting
              </div>
              <VSelect
                v-model="orderBy"
                class="mb-2"
                label="Order By"
                item-title="text"
                item-value="value"
                :items="[{text:'Bookings (Total)', value: 'bookingsTotal'},{text:'Bookings (90d)', value: 'bookingsLast90d'},{text:'Bookings (1y)', value: 'bookingsLast365d'},{text:'Favorites', value: 'favoritesCount'},{text:'Connections', value: 'followingCount'},{text:'Avg Spend', value: 'avgSpendPerPerson'},{text:'Created At', value: 'createdAt'},{text:'Last Name', value: 'lastName'},{text:'Email', value: 'email'},{text:'Member Since', value: 'memberSince'},{text:'Renewal Date', value: 'premiumRenewalDate'}]"
                required
              />
              <VRadioGroup
                v-model="orderDirection"
                row
              >
                <!--                  <template v-slot:label> -->
                <!--                    <div>Direction</div> -->
                <!--                  </template> -->
                <VRadio
                  label="▲ Ascending"
                  value=""
                  density="compact"
                />
                <VRadio
                  label="▼ Descending"
                  value="-"
                  density="compact"
                />
              </VRadioGroup>

              <VBtn
                class="account-card-primary-action-button"
                @click="usersStore.adminNewUser()"
              >
                <VIcon

                  start
                  size="18"
                  icon="mdi-add-circle-outline"
                />
                <span>New Member</span>
              </VBtn>
            </div>
          </div>
        </VCard>
      </div>
      <div>
        <VCard class="all-members-main-card">
          <VRow>
            <h3
              v-if="myUser.userLevel == 'admin'"
              class="user-dashboard-card-title mb-1"
            >
              Members ({{ totalCount }})
            </h3>
            <VSpacer />
            <VBtn
              color="grey-lighten-1"
              :icon="exporting ? '' : 'mdi-file-export'"
              :loading="exporting"
              variant="text"
              :disabled="exporting"
              @click="exportFilteredUsers"
            />
          </VRow>
          <VTextField
            v-model="searchValue"
            :loading="fetching"
            density="compact"
            variant="solo"
            :label="(fetching ? 'Syncing... ' : 'Search by name etc.')"
            append-inner-icon="mdi-magnify"
            single-line
            hide-details
            class="mb-4"
            clearable
          />
          <VTabs
            v-model="activeTab"
            class="mb-4"
          >
            <VTab value="Platinum">
              Platinum
            </VTab>
            <VTab value="Standard">
              Standard
            </VTab>
            <VTab value="Pending Approval">
              Pending
            </VTab>
          </VTabs>
          <VProgressLinear
            v-if="fetching"
            indeterminate
            color="primary"
            class="mb-2"
          />
          <!-- Cohort Stats Panel -->
          <div
            v-if="cohortStats && activeTab !== 'Pending Approval'"
            class="cohort-stats-panel mb-4"
          >
            <div class="cohort-stats-header text-caption text-medium-emphasis mb-2">
              Cohort Averages / Medians ({{ cohortStats.count }} members)
            </div>
            <div class="cohort-stats-grid">
              <div class="cohort-stat-item">
                <div class="cohort-stat-label">
                  Connections
                </div>
                <div class="cohort-stat-value">
                  {{ cohortStats.friends.avg }} / {{ cohortStats.friends.median }}
                </div>
              </div>
              <div class="cohort-stat-item">
                <div class="cohort-stat-label">
                  Favorites
                </div>
                <div class="cohort-stat-value">
                  {{ cohortStats.favorites.avg }} / {{ cohortStats.favorites.median }}
                </div>
              </div>
              <div class="cohort-stat-item">
                <div class="cohort-stat-label">
                  Bookings (90d)
                </div>
                <div class="cohort-stat-value">
                  {{ cohortStats.bookings90d.avg }} / {{ cohortStats.bookings90d.median }}
                </div>
              </div>
              <div class="cohort-stat-item">
                <div class="cohort-stat-label">
                  Bookings (1y)
                </div>
                <div class="cohort-stat-value">
                  {{ cohortStats.bookings365d.avg }} / {{ cohortStats.bookings365d.median }}
                </div>
              </div>
              <div class="cohort-stat-item">
                <div class="cohort-stat-label">
                  Bookings (All)
                </div>
                <div class="cohort-stat-value">
                  {{ cohortStats.bookingsTotal.avg }} / {{ cohortStats.bookingsTotal.median }}
                </div>
              </div>
              <div class="cohort-stat-item">
                <div class="cohort-stat-label">
                  Avg Spend/Person
                </div>
                <div class="cohort-stat-value">
                  ${{ cohortStats.avgSpend.avg }} / ${{ cohortStats.avgSpend.median }}
                </div>
              </div>
              <div
                v-if="cohortStats.leadTimeDays"
                class="cohort-stat-item"
              >
                <div class="cohort-stat-label">
                  Lead Time (days)
                </div>
                <div class="cohort-stat-value">
                  {{ cohortStats.leadTimeDays.avg }}d / {{ cohortStats.leadTimeDays.median }}d
                </div>
              </div>
            </div>
          </div>
          <div>
            <template
              v-for="user in displayedUsers"
              :key="user.email"
            >
              <VCard
                class="mb-4"
              >
                <VMenu>
                  <template #activator="{ props }">
                    <VBtn
                      density="compact"
                      class="admin-member-menu-button"
                      icon="mdi-dots-vertical"
                      v-bind="props"
                    />
                  </template>

                  <VList>
                    <VListItem
                      v-for="(item, i) in [
                        { title: 'Edit', action: function (){usersStore.adminEditUser(user._id)}},
                        { title: 'Membership', action: function (){usersStore.adminEditUserSubscription(user._id)}},
                        { title: user.accountApproved !== false ? 'Unapprove Account' : 'Approve Account', action: function (){toggleUserApproval(user._id)}},
                        { title: 'Manage Friends', action: function (){userGroupsStore.adminEditUserGroupMembers(user.friendsUserGroup)}},
                        { title: 'New Reservation', action: function (){reservationsStore.newDraftReservation([{email:user.email, permission:'owner'}, {email: myUser.email, permission:'admin'}], {email: myUser.email, firstName: myUser.firstName, lastName: myUser.lastName})}} ,
                        { title: 'Profile', action: function (){openProfile(user)} },
                        { title: 'Profile Summary', action: function (){openProfileSummary(user)} },
                        { title: 'Simulation', action: function (){simulation(user.email)} },
                        { title: 'Delete', action: function (){ isConfirmDialogOpen = true }} ]"
                      :key="i"
                      @click="selectedUser = user; item.action();"
                    >
                      <VListItemTitle>{{ item.title }}</VListItemTitle>
                    </VListItem>
                  </VList>
                </VMenu>

                <VCardItem class="pb-0">
                  <div v-if="user.fullName && user.fullName != ' '">
                    <a
                      :href="`/m/${user.profileId}`"
                      target="_blank"
                      @click.stop=""
                    ><b>{{ user.fullName }}</b><VIcon size="x-small">mdi-external-link</VIcon></a>
                    <VIcon
                      v-if="user?.verified"
                      class="ml-1 verified-icon"
                    >
                      material-symbols:verified
                    </VIcon>
                    <VIcon
                      v-else-if="user?.pendingVerification"
                      class="ml-1 verified-icon-pending"
                    >
                      mdi-progress-check
                    </VIcon>
                    <VIcon
                      v-if="user.accountApproved !== false"
                      class="ml-1 approved-icon"
                      title="Account Approved"
                    >
                      mdi-check-circle
                    </VIcon>
                    <VIcon
                      v-else
                      class="ml-1 not-approved-icon"
                      title="Account Not Approved"
                    >
                      mdi-close-circle
                    </VIcon>
                  </div>
                  <div v-else>
                    <i><b>No name added</b></i>
                  </div>

                  <VCardSubtitle>{{ user.email }}</VCardSubtitle>
                  <div
                    v-if="user.pendingPlatinumApproval && !user.platinumApproved"
                    class="text-caption font-weight-bold text-info d-flex align-center"
                  >
                    Platinum upgrade requested
                    <VBtn
                      size="x-small"
                      variant="text"
                      color="secondary"
                      class="ml-1"
                      title="Dismiss this request without granting a membership"
                      @click.stop="usersStore.adminClearPlatinumUpgradeRequest(user._id).then(() => user.pendingPlatinumApproval = false)"
                    >
                      Dismiss
                    </VBtn>
                  </div>
                  <div
                    v-if="user.title || user.company"
                    class="text-caption text-medium-emphasis"
                  >
                    {{ [user.title, user.company].filter(Boolean).join(', ') }}
                  </div>
                  <div
                    v-if="user.verifiedTags && user.verifiedTags.length > 0"
                    class="text-caption"
                  >
                    Tags: {{ user.verifiedTags.map((tag) => tag.tagName).join(', ') }}
                  </div>
                  <template v-else>
                    <div
                      v-if="user.verificationType"
                      class="text-caption"
                    >
                      verificationType: {{ user.verificationType }}
                    </div>
                    <div
                      v-if="user.verificationInfo1Data"
                      class="text-caption"
                    >
                      verificationInfo: {{ user.verificationInfo1Data }}, {{ user.verificationInfo2Data }}
                    </div>
                  </template>
                  <div
                    v-if="user.requestedTags?.length > 0"
                    class="text-caption text-warning"
                  >
                    {{ user.requestedTags.length }} pending tag request(s)
                  </div>
                </VCardItem>

                <VCardText class="pt-1 pb-3">
                  <div
                    v-if="user.bio"
                    class="member-card-bio text-caption text-medium-emphasis mb-1"
                  >
                    {{ user.bio }}
                  </div>
                  <div class="member-card-grid">
                    <div>
                      {{ (user.primaryGroupName || 'Individual') + ' (' + user.membershipType + ') ' + (user.isSecondaryAccount ? '- Secondary' : '- Primary') }}
                    </div>
                    <div>
                      <span v-if="user.memberSince">Since: {{ moment(user.memberSince).format("M/D/YY") }}</span>
                    </div>
                    <div>
                      <span v-if="user.platinumRenewalDate">Renewal: {{ moment(user.platinumRenewalDate).format("M/D/YY") }}</span>
                      <span v-else-if="user.platinumMembershipStatus == 'expired' && user.platinumExpiredOn">Expired: {{ moment(user.platinumExpiredOn).format("M/D/YY") }}</span>
                    </div>
                    <div v-if="user.createdAt || user.subscriptionStart">
                      Joined: {{ moment(user.createdAt || user.subscriptionStart).format("M/D/YY") }}
                    </div>
                    <div>
                      <span v-if="user.lastActiveAt">Last active: {{ moment(user.lastActiveAt).fromNow() }}</span>
                      <span
                        v-else
                        class="text-medium-emphasis"
                      >Last active: prior to 2/23/26</span>
                    </div>
                    <div>
                      Bookings: {{ user.bookingsLast90d || 0 }}/{{ user.bookingsLast365d || 0 }}/{{ user.bookingsTotal || 0 }}
                      <span class="text-caption text-medium-emphasis">(90d/1y/all)</span>
                    </div>
                    <div>
                      Favorites: {{ user.favoritesCount || 0 }}, Connections: {{ user.followingCount || 0 }}
                    </div>
                    <div v-if="user.profileLinkedInProfileId || user.profileInstagramProfileId || user.profileWebsiteLink">
                      <a
                        v-if="user.profileLinkedInProfileId"
                        :href="`https://linkedin.com/in/${user.profileLinkedInProfileId}`"
                        target="_blank"
                        class="member-card-social-link"
                        @click.stop=""
                      >LI</a>
                      <a
                        v-if="user.profileInstagramProfileId"
                        :href="`https://instagram.com/${user.profileInstagramProfileId}`"
                        target="_blank"
                        class="member-card-social-link"
                        @click.stop=""
                      >IG</a>
                      <a
                        v-if="user.profileWebsiteLink"
                        :href="getFullUrl(user.profileWebsiteLink)"
                        target="_blank"
                        class="member-card-social-link"
                        @click.stop=""
                      >Web</a>
                    </div>
                    <div v-if="user.secondaryAccountMemberName">
                      Secondary: {{ user.secondaryAccountMemberName }}
                    </div>
                    <div v-else-if="user.primaryAccountMemberName">
                      Primary: {{ user.primaryAccountMemberName }}
                    </div>
                    <div v-else>
                      Secondary: None
                    </div>
                  </div>
                  <div
                    v-if="user.profileId"
                    class="member-card-referral-link mt-1 d-flex align-center flex-wrap"
                  >
                    <a
                      :href="`/${user.profileId}/friends`"
                      target="_blank"
                      class="member-card-social-link text-caption"
                      @click.stop=""
                    >reserverm.com/{{ user.profileId }}/friends</a>
                    <span
                      class="copy-referral-btn"
                      :class="{ 'copied': copiedReferralUserId === user._id }"
                      @click.stop="copyReferralMessage(user)"
                    >
                      <VIcon
                        size="14"
                        :icon="copiedReferralUserId === user._id ? 'mdi-check' : 'mdi-content-copy'"
                      />
                      <span class="copy-referral-label">{{ copiedReferralUserId === user._id ? 'Copied!' : 'Copy Share Message' }}</span>
                    </span>
                  </div>
                  <div
                    v-if="user.profileId"
                    class="member-card-referral-link mt-1"
                  >
                    <a
                      :href="`/m/${user.profileId}`"
                      target="_blank"
                      class="member-card-social-link text-caption"
                      @click.stop=""
                    >reserverm.com/m/{{ user.profileId }}</a>
                  </div>
                </VCardText>
              </VCard>
            </template>

            <div
              v-if="loadingMore"
              class="d-flex justify-center py-4"
            >
              <VProgressCircular
                indeterminate
                color="primary"
                size="32"
              />
            </div>
            <div
              v-if="hasMore && displayedUsers.length > 0"
              ref="scrollSentinel"
              class="scroll-sentinel"
            />
          </div>
        </VCard>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Mobile-specific styles for filters */
@media only screen and (max-width: 767px) {
  .account-card {
    flex-direction: column;
  }

  .account-card-content {
    width: 100%;
  }

  :deep(.v-select) {
    width: 100%;
    margin-top: 0.75rem !important;
  }
}

.pagination .item {
  margin: 10px;
}
.item.active {
  color: red;
}

.admin-member-menu-button {
  position: absolute;
  float: right;
  top: 0;
  right: 0;
  margin: var(--ds-space-5);
}

.verified-icon {
  color: rgb(0, 149, 246);
  width: 1.25rem;
  height: 1.25rem;
  margin-bottom: 5px;
}

.verified-icon-pending {
  cursor: pointer;
  color: #ffd900;
  width: 1.25rem;
  height: 1.25rem;
}

.approved-icon {
  color: #4caf50;
  width: 1.25rem;
  height: 1.25rem;
  margin-bottom: 5px;
}

.not-approved-icon {
  color: #f44336;
  width: 1.25rem;
  height: 1.25rem;
  margin-bottom: 5px;
}

.back-button-container {
  padding: 0 var(--ds-space-1);
}

.back-button {
  padding: var(--ds-space-2) var(--ds-space-3);
  color: var(--ds-text-strong);
  text-decoration: none;
  font-size: var(--ds-text-base);
  transition: background-color var(--ds-duration-normal);
  border-radius: var(--ds-radius-xs);
  display: inline-flex;
}

.back-button:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.back-button svg {
  color: var(--ds-text-secondary);
}

.profile-summary-dialog {
  border-radius: var(--ds-radius-md) !important;
  overflow: hidden;
}

.profile-summary-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--ds-space-4) var(--ds-space-4) var(--ds-space-4) var(--ds-space-6);
}

.profile-summary-dialog-title {
  font-size: var(--ds-text-lg);
  font-weight: 600;
  color: var(--ds-text-primary);
}

.profile-summary-content {
  font-size: var(--ds-text-base);
  color: var(--ds-text-primary);
  line-height: 1.7;
}

.profile-summary-avatar {
  width: 4.5rem;
  height: 4.5rem;
  flex-shrink: 0;
  background: var(--ds-brand-primary-light);
  outline: thin solid rgba(69, 182, 169, 0.25);
  outline-offset: 0.2rem;
}

.profile-summary-avatar-img {
  border-radius: var(--ds-radius-full);
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-summary-avatar-fallback {
  width: 100%;
  height: 100%;
  font-size: 1.5rem;
  color: var(--ds-text-secondary);
}

.profile-summary-name {
  font-size: var(--ds-text-xl);
  font-weight: 600;
  color: var(--ds-text-strong);
}

.profile-summary-line {
  color: var(--ds-text-primary);
}

.profile-summary-tags {
  border-top: 1px solid var(--ds-border-subtle);
  padding-top: var(--ds-space-3);
}

.profile-summary-links {
  border-top: 1px solid var(--ds-border-subtle);
  padding-top: var(--ds-space-3);
}

.profile-summary-link-line {
  padding: 3px 0;
  color: var(--ds-text-primary);
}

.profile-summary-link-line a {
  color: #1a73e8;
  text-decoration: none;
}

.profile-summary-link-line a:hover {
  text-decoration: underline;
}

.profile-summary-tag-line {
  padding: 3px 0;
  color: var(--ds-text-primary);
}

.member-card-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px var(--ds-space-4);
  font-size: var(--ds-text-sm);
  line-height: var(--ds-leading-normal);
}

@media only screen and (max-width: 899px) {
  .member-card-grid {
    grid-template-columns: 1fr;
  }
}

.member-card-social-link {
  color: var(--ds-brand-primary-dark);
  text-decoration: none;
  font-size: var(--ds-text-xs);
  margin-right: var(--ds-space-2);
}

.member-card-social-link:hover {
  text-decoration: underline;
}

.copy-referral-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--ds-space-1);
  margin-left: var(--ds-space-2);
  padding: 2px var(--ds-space-2);
  border-radius: var(--ds-radius-xs);
  border: 1px solid var(--ds-border-light);
  background-color: var(--ds-surface-card);
  cursor: pointer;
  color: var(--ds-text-muted);
  font-size: var(--ds-text-xs);
  transition: all var(--ds-duration-normal);

  &:hover {
    background-color: rgb(248, 249, 250);
    border-color: var(--ds-brand-primary);
    color: var(--ds-brand-primary-dark);
  }

  &.copied {
    color: rgb(76, 175, 80);
    border-color: rgb(76, 175, 80);
    background-color: rgb(232, 245, 233);
  }
}

.copy-referral-label {
  white-space: nowrap;
}

.scroll-sentinel {
  height: 1px;
}

.member-card-bio {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.cohort-stats-panel {
  background: var(--ds-surface-subtle);
  border: 1px solid var(--ds-border-light);
  border-radius: var(--ds-radius-sm);
  padding: var(--ds-space-3) var(--ds-space-4);
}

.cohort-stats-header {
  font-weight: 600;
}

.cohort-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--ds-space-2) var(--ds-space-4);
}

@media only screen and (max-width: 599px) {
  .cohort-stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.cohort-stat-item {
  display: flex;
  flex-direction: column;
}

.cohort-stat-label {
  font-size: var(--ds-text-xs);
  color: var(--ds-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.cohort-stat-value {
  font-size: var(--ds-text-base);
  font-weight: 600;
  color: var(--ds-text-primary);
}

.all-members-main-card {
  padding: 2.5rem;
  padding-top: 2rem;
  overflow: hidden;
}

@media only screen and (max-width: 1023px) {
  .all-members-main-card {
    padding: 1rem;
    padding-top: 1rem;
  }
}

@media only screen and (max-width: 599px) {
  .all-members-main-card {
    padding: 0.75rem;
    padding-top: 0.75rem;
  }
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
  # Platform-admin-only: this is the admin members MANAGEMENT console (it calls
  # admin-only store actions — getAdminUsers/adminEditUser/adminDeleteUser/
  # adminNewUser/subscriptions/simulate). The matching APIs enforce
  # requireAdmin(); requiresAdmin gates the route to match (H10).
  requiresAdmin: true
</route>
